import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Search, TrendingUp, Link2, FileText, MapPin, DollarSign, BarChart3, 
  Plus, Globe, Target, CheckCircle2, AlertCircle, Clock, ArrowRight,
  Sparkles, RefreshCw, ExternalLink, Settings, Trash2, Edit, Eye,
  ChevronRight, Zap, Shield, Star, Map, Building, Award, BookOpen,
  ListChecks, FileSearch, Share2, Megaphone, ChevronDown, ChevronUp,
  Download, Calendar, Play, Copy, CheckCheck, Lightbulb, BookMarked,
  X, ArrowLeft, Lock, Users, LineChart, MousePointer, Crown, Gift, CreditCard
} from 'lucide-react';
import { api, useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// ==================== FREE TOOLS SECTION ====================
const FreeToolsSection = () => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolInput, setToolInput] = useState('');
  const [toolLoading, setToolLoading] = useState(false);
  const [toolResult, setToolResult] = useState(null);
  const [showProspectingWidget, setShowProspectingWidget] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);

  useEffect(() => {
    if (showProspectingWidget) {
      const existingScript = document.querySelector('script[src*="prospecting/client/widget_script.js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://services.leadconnectorhq.com/prospecting/client/widget_script.js';
        script.type = 'text/javascript';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [showProspectingWidget]);

  // Fetch history when toggling history view
  const fetchHistory = async (toolType = null) => {
    setHistoryLoading(true);
    try {
      const response = await api.getToolHistory(toolType);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleToggleHistory = () => {
    if (!showHistory) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
    setSelectedHistoryEntry(null);
  };

  const handleViewHistoryEntry = (entry) => {
    setSelectedHistoryEntry(entry);
    setSelectedTool(entry.tool_type);
    setToolInput(entry.input_value);
    setToolResult(entry.result);
    setShowHistory(false);
  };

  const handleDeleteHistoryEntry = async (entryId, e) => {
    e.stopPropagation();
    try {
      await api.deleteToolHistoryEntry(entryId);
      setHistory(history.filter(h => h.id !== entryId));
      toast.success('History entry deleted');
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const tools = [
    { id: 'page-auditor', name: 'Page Auditor', icon: FileSearch, description: 'Analyze any webpage for SEO issues', color: 'from-cyan-500 to-blue-600' },
    { id: 'full-audit', name: 'Full Business Audit', icon: Building, description: 'Comprehensive audit with Tech Stack, GBP, Reviews & more', color: 'from-amber-500 to-orange-600' },
    { id: 'keyword-research', name: 'Keyword Research', icon: Search, description: 'Find keywords with AI suggestions', color: 'from-purple-500 to-pink-600' },
    { id: 'meta-analyzer', name: 'Meta Tags Analyzer', icon: FileText, description: 'Analyze meta tags of any page', color: 'from-rose-500 to-red-600' },
  ];

  const handleToolSelect = (toolId) => {
    setSelectedTool(toolId);
    setToolResult(null);
    setSelectedHistoryEntry(null);
    setShowProspectingWidget(toolId === 'full-audit');
  };

  const handleRunTool = async () => {
    if (!toolInput) {
      toast.error('Please enter a URL or search term');
      return;
    }
    
    setToolLoading(true);
    setToolResult(null);
    setSelectedHistoryEntry(null);
    
    try {
      let result;
      switch (selectedTool) {
        case 'page-auditor':
          result = await api.runAIPageAudit({ url: toolInput });
          break;
        case 'keyword-research':
          result = await api.getAIKeywordSuggestions({ website_url: toolInput, business_type: '', location: '' });
          break;
        case 'meta-analyzer':
          result = await api.analyzeMetaTags({ url: toolInput });
          break;
        default:
          throw new Error('Unknown tool');
      }
      setToolResult(result.data);
      
      // Save to history
      try {
        await api.saveToolHistory({
          tool_type: selectedTool,
          input_value: toolInput,
          result: result.data
        });
      } catch (historyError) {
        console.error('Failed to save to history:', historyError);
      }
      
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setToolLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* History Toggle Button */}
      <div className="flex justify-end">
        <Button 
          variant={showHistory ? "default" : "outline"} 
          size="sm" 
          onClick={handleToggleHistory}
          className={showHistory ? "bg-cyan-500 hover:bg-cyan-600" : ""}
        >
          <Clock className="w-4 h-4 mr-2" />
          {showHistory ? 'Hide History' : 'View History'}
        </Button>
      </div>

      {/* History Panel */}
      {showHistory && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Analysis History
            </CardTitle>
            <CardDescription>View your past tool results</CardDescription>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No history yet. Run a tool to save results.</p>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {history.map((entry) => {
                  const toolConfig = tools.find(t => t.id === entry.tool_type) || { icon: BarChart3, color: 'from-gray-500 to-gray-600' };
                  const ToolIcon = toolConfig.icon;
                  return (
                    <div
                      key={entry.id}
                      onClick={() => handleViewHistoryEntry(entry)}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br", toolConfig.color)}>
                          <ToolIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium truncate max-w-[300px]">{entry.title}</p>
                          <p className="text-gray-500 text-xs">{new Date(entry.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 h-7 w-7"
                          onClick={(e) => handleDeleteHistoryEntry(entry.id, e)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tool Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tools.map((tool) => (
          <Card 
            key={tool.id}
            className={cn(
              "cursor-pointer transition-all hover:scale-[1.02] border-white/10",
              selectedTool === tool.id ? "ring-2 ring-primary" : "bg-white/5"
            )}
            onClick={() => handleToolSelect(tool.id)}
          >
            <CardContent className="p-4">
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-gradient-to-br", tool.color)}>
                <tool.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-white font-medium">{tool.name}</h3>
              <p className="text-gray-400 text-sm mt-1">{tool.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedTool && selectedTool !== 'full-audit' && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-3">
              <Input
                placeholder={selectedTool === 'keyword-research' ? "Enter website URL or topic..." : "Enter URL to analyze..."}
                value={toolInput}
                onChange={(e) => setToolInput(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleRunTool} disabled={toolLoading}>
                {toolLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {toolLoading ? 'Analyzing...' : 'Analyze'}
              </Button>
            </div>
            
            {toolResult && (
              <div className="mt-4 p-4 bg-white/5 rounded-lg">
                {selectedTool === 'page-auditor' && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium">SEO Score</span>
                      <span className={cn("text-2xl font-bold", toolResult.overall_score >= 80 ? "text-green-400" : toolResult.overall_score >= 60 ? "text-yellow-400" : "text-red-400")}>
                        {toolResult.overall_score}/100
                      </span>
                    </div>
                    {toolResult.issues?.critical?.map((issue, i) => (
                      <div key={i} className="text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-3 h-3" /> {issue}</div>
                    ))}
                  </div>
                )}
                {selectedTool === 'keyword-research' && toolResult.keywords && (
                  <div className="space-y-3">
                    <span className="text-white font-medium">Suggested Keywords ({toolResult.keywords.length})</span>
                    <div className="space-y-2 mt-2">
                      {toolResult.keywords.map((kw, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                          <span className="text-cyan-400 font-medium">{typeof kw === 'object' ? kw.keyword : kw}</span>
                          {typeof kw === 'object' && (
                            <div className="flex items-center gap-2 text-xs">
                              <Badge variant="outline" className="text-gray-400 border-gray-500/30">{kw.volume || 'N/A'}</Badge>
                              <Badge variant="outline" className={cn(
                                "border-opacity-30",
                                kw.difficulty < 30 ? "text-green-400 border-green-400" : 
                                kw.difficulty < 60 ? "text-yellow-400 border-yellow-400" : 
                                "text-red-400 border-red-400"
                              )}>{kw.difficulty || 'N/A'}/100</Badge>
                              <Badge variant="outline" className="text-purple-400 border-purple-400/30 capitalize">{kw.intent || 'N/A'}</Badge>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {toolResult.strategy_tips && toolResult.strategy_tips.length > 0 && (
                      <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                        <span className="text-amber-400 font-medium text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Strategy Tips</span>
                        <ul className="mt-2 space-y-1 text-sm text-gray-300">
                          {toolResult.strategy_tips.map((tip, i) => (
                            <li key={i}>• {tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {selectedTool === 'meta-analyzer' && (
                  <div className="space-y-3">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <span className="text-gray-400 text-sm">Title</span>
                      <p className="text-white mt-1">{toolResult.meta_tags?.title || toolResult.title || 'Not found'}</p>
                      <span className="text-xs text-gray-500">{toolResult.analysis?.title_length || 0} characters</span>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <span className="text-gray-400 text-sm">Description</span>
                      <p className="text-white mt-1">{toolResult.meta_tags?.description || toolResult.description || 'Not found'}</p>
                      <span className="text-xs text-gray-500">{toolResult.analysis?.description_length || 0} characters</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className={cn("p-2 rounded text-center text-sm", toolResult.analysis?.has_og_tags ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400")}>
                        {toolResult.analysis?.has_og_tags ? <CheckCircle2 className="w-4 h-4 mx-auto mb-1" /> : <AlertCircle className="w-4 h-4 mx-auto mb-1" />}
                        Open Graph
                      </div>
                      <div className={cn("p-2 rounded text-center text-sm", toolResult.analysis?.has_twitter_cards ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400")}>
                        {toolResult.analysis?.has_twitter_cards ? <CheckCircle2 className="w-4 h-4 mx-auto mb-1" /> : <AlertCircle className="w-4 h-4 mx-auto mb-1" />}
                        Twitter Cards
                      </div>
                      <div className={cn("p-2 rounded text-center text-sm", toolResult.analysis?.has_canonical ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400")}>
                        {toolResult.analysis?.has_canonical ? <CheckCircle2 className="w-4 h-4 mx-auto mb-1" /> : <AlertCircle className="w-4 h-4 mx-auto mb-1" />}
                        Canonical
                      </div>
                    </div>
                    {toolResult.suggestions && toolResult.suggestions.length > 0 && (
                      <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                        <span className="text-cyan-400 font-medium text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4" /> AI Suggestions</span>
                        <ul className="mt-2 space-y-1 text-sm text-gray-300">
                          {toolResult.suggestions.filter(s => s.trim()).map((suggestion, i) => (
                            <li key={i}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showProspectingWidget && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-6">
            <div className="mb-4">
              <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-amber-400" />
                Full Business Audit
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Get a comprehensive analysis of your business including Tech Stack, Google Business Profile, Reviews, and more.
              </p>
            </div>
            <div 
              id="lead-connector-widget-container"
              className="min-h-[500px] rounded-lg bg-white/5"
              ref={(el) => {
                if (el && !el.querySelector('script[data-widget-id]')) {
                  const script = document.createElement('script');
                  script.src = 'https://services.leadconnectorhq.com/prospecting/client/widget-embed.js';
                  script.setAttribute('data-widget-id', '693eeaca79dc588f8409fbab');
                  script.async = true;
                  el.appendChild(script);
                }
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== SOP VIEWER ====================
const SOPViewer = ({ projectId }) => {
  const [sops, setSOPs] = useState([]);
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSOPs();
  }, []);

  const fetchSOPs = async () => {
    try {
      const response = await api.getSOPs();
      setSOPs(response.data);
      if (response.data.length > 0) setSelectedSOP(response.data[0]);
    } catch (error) {
      console.error('Failed to fetch SOPs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1 space-y-2">
        <h3 className="text-white font-medium mb-3">Available SOPs</h3>
        {sops.map((sop) => (
          <button
            key={sop.id}
            onClick={() => setSelectedSOP(sop)}
            className={cn(
              "w-full text-left p-3 rounded-lg transition-colors",
              selectedSOP?.id === sop.id ? "bg-primary/20 text-primary" : "bg-white/5 text-gray-300 hover:bg-white/10"
            )}
          >
            <div className="font-medium text-sm">{sop.title}</div>
            <div className="text-xs text-gray-500 mt-1">{sop.estimated_time}</div>
          </button>
        ))}
      </div>

      <div className="lg:col-span-3">
        {selectedSOP && (
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">{selectedSOP.title}</CardTitle>
              <CardDescription>{selectedSOP.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedSOP.steps?.map((step, stepIdx) => (
                <div key={stepIdx} className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-white font-medium mb-2">Step {step.step_number}: {step.title}</h4>
                  <p className="text-gray-400 text-sm mb-3">{step.description}</p>
                  <div className="space-y-2">
                    {step.checklist?.map((item, itemIdx) => (
                      <div key={itemIdx} className="flex items-center gap-2 text-sm">
                        <Checkbox className="border-gray-600" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ==================== PROJECT CARD ====================
const ProjectCard = ({ project, onView, onDelete, onToggleMode, onSubscribe }) => {
  const isDFY = project.management_type === 'dfy';
  
  return (
    <Card className="bg-white/5 border-white/10 hover:border-white/20 transition-all">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", isDFY ? "bg-gradient-to-br from-amber-500 to-orange-600" : "bg-gradient-to-br from-cyan-500 to-blue-600")}>
            {isDFY ? <Crown className="w-5 h-5 text-white" /> : <Target className="w-5 h-5 text-white" />}
          </div>
          <Badge variant={isDFY ? "default" : "outline"} className={isDFY ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : ""}>
            {isDFY ? 'Done For You' : 'Do It Yourself'}
          </Badge>
          {isDFY && project.subscription_status === 'active' && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>
          )}
        </div>
        
        <h3 className="text-white font-semibold text-lg mb-1">{project.project_name || project.name}</h3>
        {project.ordered_by && (
          <p className="text-cyan-400 text-xs mb-1">{project.ordered_by}</p>
        )}
        <p className="text-gray-400 text-sm mb-1 truncate">{project.website_url}</p>
        {project.ordered_date && (
          <p className="text-gray-500 text-xs mb-3">Ordered {project.ordered_date}</p>
        )}
        
        {isDFY && project.trial_end && !project.subscription_status && (
          <div className={cn("text-xs mb-3", new Date(project.trial_end) < new Date() ? "text-red-400" : "text-amber-400")}>
            {new Date(project.trial_end) < new Date() ? (
              <span>Trial ended - Subscribe to continue</span>
            ) : (
              <span>Trial ends: {new Date(project.trial_end).toLocaleDateString()}</span>
            )}
          </div>
        )}
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => onView(project)}>
            <Eye className="w-3 h-3 mr-1" /> View
          </Button>
          {isDFY && !project.subscription_status && new Date(project.trial_end) < new Date() ? (
            <Button size="sm" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500" onClick={() => onSubscribe && onSubscribe(project)}>
              <CreditCard className="w-3 h-3 mr-1" /> Subscribe
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => onToggleMode(project)}>
              <RefreshCw className="w-3 h-3 mr-1" /> {isDFY ? 'Switch to DIY' : 'Upgrade to DFY'}
            </Button>
          )}
          <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300" onClick={() => onDelete(project)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== CREATE PROJECT DIALOG ====================
const CreateProjectDialog = ({ open, onOpenChange, onCreated, pricing }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    project_name: '',
    website_url: '',
    management_type: 'diy', // 'diy' or 'dfy'
    service_type: 'seo' // For DFY: 'seo' or 'ppc'
  });

  const handleCreate = async () => {
    if (!formData.project_name || !formData.website_url) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      if (formData.management_type === 'dfy') {
        // Create DFY project
        const response = await api.createDFYProject({
          project_name: formData.project_name,
          website_url: formData.website_url.startsWith('http') ? formData.website_url : `https://${formData.website_url}`,
          service_type: formData.service_type
        });
        toast.success('DFY project created! Your 7-day free trial has started.');
        onCreated({ ...response.data, management_type: 'dfy' });
      } else {
        // Create DIY project (existing ranking project)
        const response = await api.createRankingProject({
          project_name: formData.project_name,
          website_url: formData.website_url.startsWith('http') ? formData.website_url : `https://${formData.website_url}`
        });
        toast.success('DIY project created!');
        onCreated({ ...response.data, management_type: 'diy' });
      }
      onOpenChange(false);
      setStep(1);
      setFormData({ project_name: '', website_url: '', management_type: 'diy', service_type: 'seo' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white">
            {step === 1 ? 'Create New Project' : step === 2 ? 'Choose Your Path' : 'Start Free Trial'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 ? 'Enter your project details' : step === 2 ? 'Do it yourself or let us handle it' : 'Your 7-day free trial includes everything'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input
                placeholder="My Business Website"
                value={formData.project_name}
                onChange={(e) => setFormData({...formData, project_name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Website URL</Label>
              <div className="flex">
                <span className="flex items-center px-3 bg-white/10 border border-r-0 border-white/20 rounded-l-md text-gray-400 text-sm">https://</span>
                <Input
                  placeholder="example.com"
                  className="rounded-l-none"
                  value={formData.website_url.replace(/^https?:\/\//, '')}
                  onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <div 
              className={cn(
                "p-4 rounded-lg border-2 cursor-pointer transition-all",
                formData.management_type === 'diy' ? "border-cyan-500 bg-cyan-500/10" : "border-white/10 hover:border-white/30"
              )}
              onClick={() => setFormData({...formData, management_type: 'diy'})}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center", formData.management_type === 'diy' ? "border-cyan-500 bg-cyan-500" : "border-white/30")}>
                  {formData.management_type === 'diy' && <CheckCircle2 className="w-3 h-3 text-white" />}
                </div>
                <span className="text-white font-semibold">Do It Yourself (DIY)</span>
                <Badge variant="outline" className="ml-auto">FREE</Badge>
              </div>
              <p className="text-gray-400 text-sm ml-8">Access all tools, SOPs, and guides. Manage everything yourself with our AI-powered toolkit.</p>
            </div>

            <div 
              className={cn(
                "p-4 rounded-lg border-2 cursor-pointer transition-all",
                formData.management_type === 'dfy' ? "border-amber-500 bg-amber-500/10" : "border-white/10 hover:border-white/30"
              )}
              onClick={() => setFormData({...formData, management_type: 'dfy'})}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center", formData.management_type === 'dfy' ? "border-amber-500 bg-amber-500" : "border-white/30")}>
                  {formData.management_type === 'dfy' && <CheckCircle2 className="w-3 h-3 text-white" />}
                </div>
                <span className="text-white font-semibold">Done For You (DFY)</span>
                <Badge className="ml-auto bg-amber-500/20 text-amber-400 border-amber-500/30">7-DAY FREE TRIAL</Badge>
              </div>
              <p className="text-gray-400 text-sm ml-8">We handle everything for you. Just approve content and watch your rankings grow.</p>
              
              {formData.management_type === 'dfy' && (
                <div className="mt-4 ml-8 space-y-3">
                  <Label className="text-white text-sm">Select Service Type:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      className={cn("p-3 rounded-lg border text-left", formData.service_type === 'seo' ? "border-cyan-500 bg-cyan-500/10" : "border-white/10")}
                      onClick={(e) => { e.stopPropagation(); setFormData({...formData, service_type: 'seo'}); }}
                    >
                      <div className="text-white font-medium">SEO</div>
                      <div className="text-gray-400 text-xs">Rankings & organic traffic</div>
                      {pricing && <div className="text-cyan-400 text-sm mt-1">${pricing.seo_price}/mo after trial</div>}
                    </button>
                    <button
                      className={cn("p-3 rounded-lg border text-left", formData.service_type === 'ppc' ? "border-purple-500 bg-purple-500/10" : "border-white/10")}
                      onClick={(e) => { e.stopPropagation(); setFormData({...formData, service_type: 'ppc'}); }}
                    >
                      <div className="text-white font-medium">PPC</div>
                      <div className="text-gray-400 text-xs">Google Ads management</div>
                      {pricing && <div className="text-purple-400 text-sm mt-1">${pricing.ppc_price}/mo after trial</div>}
                    </button>
                  </div>
                  
                  {pricing && pricing.plan !== 'concierge' && (
                    <div className="p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/30">
                      <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                        <Gift className="w-4 h-4" />
                        Save money by upgrading your plan!
                      </div>
                      <div className="text-gray-400 text-xs mt-1">
                        {(pricing.plan === 'starter' || pricing.plan === 'activation') && 'Contact us about the Partner Plan for best value'}
                        {pricing.plan === 'growth' && 'You are on the Partner Plan — best value!'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && formData.management_type === 'dfy' && (
          <div className="space-y-4 py-4">
            <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{formData.project_name}</h4>
                  <p className="text-gray-400 text-sm">{formData.service_type.toUpperCase()} Management</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white/5 rounded text-center">
                  <div className="text-green-400 font-medium">7 Days</div>
                  <div className="text-gray-500 text-xs">Free Trial</div>
                </div>
                <div className="p-2 bg-white/5 rounded text-center">
                  <div className="text-white font-medium">${pricing?.[`${formData.service_type}_price`] || 99}/mo</div>
                  <div className="text-gray-500 text-xs">After Trial</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-white text-sm font-medium">What&apos;s Included:</h4>
              <ul className="space-y-1">
                {(formData.service_type === 'seo' ? ['Keyword Research & Tracking', 'On-Page SEO Optimization', 'Technical SEO Audits', 'Backlink Monitoring', 'Content Strategy', 'Monthly Reports'] : ['Google Ads Campaign Setup', 'Keyword Research', 'Ad Copy Creation', 'Bid Management', 'Performance Tracking', 'Monthly Reports']).map((f, i) => (
                  <li key={i} className="text-gray-300 text-sm flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-green-400" />{f}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>
          )}
          {step < (formData.management_type === 'dfy' ? 3 : 2) ? (
            <Button onClick={() => setStep(step + 1)} disabled={step === 1 && (!formData.project_name || !formData.website_url)}>
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              {formData.management_type === 'dfy' ? 'Start Free Trial' : 'Create Project'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== DIY PROJECT DETAIL VIEW ====================
const DIYProjectDetailView = ({ project, onBack }) => {
  const [activeTab, setActiveTab] = useState('keywords');
  const [keywords, setKeywords] = useState([]);
  const [content, setContent] = useState([]);
  const [contentFilter, setContentFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddKeyword, setShowAddKeyword] = useState(false);
  const [showAddContent, setShowAddContent] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newContent, setNewContent] = useState({ title: '', content_type: 'blog', target_keyword: '', scheduled_date: '' });

  const fetchData = useCallback(async () => {
    try {
      const [kwRes, contentRes] = await Promise.all([
        api.getRankingKeywords(project.id),
        api.getRankingContent(project.id)
      ]);
      setKeywords(kwRes.data || []);
      setContent(contentRes.data || []);
    } catch (error) {
      console.error('Failed to fetch project data:', error);
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    if (project) fetchData();
  }, [project, fetchData]);

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) return;
    try {
      await api.addRankingKeyword(project.id, { keyword: newKeyword, target_rank: 10 });
      setNewKeyword('');
      setShowAddKeyword(false);
      fetchData();
      toast.success('Keyword added!');
    } catch (error) {
      toast.error('Failed to add keyword');
    }
  };

  const handleAddContent = async () => {
    if (!newContent.title) return;
    try {
      await api.createRankingContent(project.id, newContent);
      setNewContent({ title: '', content_type: 'blog', target_keyword: '', scheduled_date: '' });
      setShowAddContent(false);
      fetchData();
      toast.success('Content added!');
    } catch (error) {
      toast.error('Failed to add content');
    }
  };

  const filteredContent = content.filter(c => {
    if (contentFilter === 'all') return true;
    return c.status === contentFilter;
  });

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects</Button>
        <div className="text-right">
          <h2 className="text-xl font-bold text-white">{project.name || project.project_name}</h2>
          <p className="text-gray-400 text-sm">{project.website_url}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="keywords">Keywords</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="sops">SOPs</TabsTrigger>
        </TabsList>

        <TabsContent value="keywords" className="mt-6">
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Tracked Keywords</CardTitle>
              <Button size="sm" onClick={() => setShowAddKeyword(true)}><Plus className="w-4 h-4 mr-1" /> Add Keyword</Button>
            </CardHeader>
            <CardContent>
              {keywords.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No keywords tracked yet. Add your first keyword to start.</p>
              ) : (
                <div className="space-y-2">
                  {keywords.map((kw) => (
                    <div key={kw.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="text-white">{kw.keyword}</span>
                      <Badge variant="outline">{kw.current_rank ? `#${kw.current_rank}` : 'Not ranked'}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={showAddKeyword} onOpenChange={setShowAddKeyword}>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader><DialogTitle className="text-white">Add Keyword</DialogTitle></DialogHeader>
              <Input placeholder="Enter keyword..." value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)} />
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddKeyword(false)}>Cancel</Button>
                <Button onClick={handleAddKeyword}>Add</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Content Calendar</CardTitle>
                <Button size="sm" onClick={() => setShowAddContent(true)}><Plus className="w-4 h-4 mr-1" /> Add Content</Button>
              </div>
              <div className="flex gap-2 mt-3">
                {['all', 'planned', 'in_progress', 'published'].map(f => (
                  <Button key={f} variant={contentFilter === f ? 'default' : 'ghost'} size="sm" onClick={() => setContentFilter(f)} className="capitalize">
                    {f.replace('_', ' ')} ({content.filter(c => f === 'all' || c.status === f).length})
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {filteredContent.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No content in this category. Add content to plan your strategy.</p>
              ) : (
                <div className="space-y-2">
                  {filteredContent.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={cn("w-8 h-8 rounded flex items-center justify-center", item.content_type === 'blog' ? "bg-green-500/20" : "bg-blue-500/20")}>
                          <FileText className={cn("w-4 h-4", item.content_type === 'blog' ? "text-green-400" : "text-blue-400")} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{item.title}</p>
                          <p className="text-gray-400 text-xs">{item.scheduled_date || 'No date set'}</p>
                        </div>
                      </div>
                      <Badge variant={item.status === 'published' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}>
                        {item.status || 'planned'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={showAddContent} onOpenChange={setShowAddContent}>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader><DialogTitle className="text-white">Add Content</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Content title..." value={newContent.title} onChange={(e) => setNewContent({...newContent, title: e.target.value})} />
                <Select value={newContent.content_type} onValueChange={(v) => setNewContent({...newContent, content_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blog">Blog Post</SelectItem>
                    <SelectItem value="social">Social Post</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="date" value={newContent.scheduled_date} onChange={(e) => setNewContent({...newContent, scheduled_date: e.target.value})} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddContent(false)}>Cancel</Button>
                <Button onClick={handleAddContent}>Add Content</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="sops" className="mt-6">
          <SOPViewer projectId={project.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== DFY PROJECT DETAIL VIEW ====================
const DFYProjectDetailView = ({ project, onBack, isDemo = false, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('onboarding');
  const [localProject, setLocalProject] = useState(project);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [showKeywordsDialog, setShowKeywordsDialog] = useState(false);
  const [showContentPrefsDialog, setShowContentPrefsDialog] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [showGSCModal, setShowGSCModal] = useState(false);
  const [showAddKeywordDialog, setShowAddKeywordDialog] = useState(false);
  const [showAddContentDialog, setShowAddContentDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  
  // Form states
  const [newKeywords, setNewKeywords] = useState('');
  const [contentPrefs, setContentPrefs] = useState({
    tone: 'professional',
    style: 'informative',
    target_audience: '',
    avoid_topics: ''
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [newContent, setNewContent] = useState({ title: '', type: 'blog' });

  const tabs = [
    { id: 'onboarding', label: 'Onboarding', icon: CheckCheck },
    { id: 'rankings', label: 'Rankings', icon: TrendingUp },
    { id: 'audits', label: 'Audits', icon: FileSearch },
    { id: 'backlinks', label: 'Backlinks', icon: Link2 },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'search_console', label: 'Search Console', icon: LineChart },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Refresh project data
  const refreshProject = async () => {
    if (isDemo) return;
    try {
      const response = await api.getDFYProject(localProject.id);
      setLocalProject(response.data);
    } catch (error) {
      console.error('Failed to refresh project:', error);
    }
  };

  // Handle adding keywords to onboarding
  const handleAddOnboardingKeywords = async () => {
    if (!newKeywords.trim()) {
      toast.error('Please enter at least one keyword');
      return;
    }
    
    setLoading(true);
    try {
      const keywordList = newKeywords.split('\n').filter(k => k.trim());
      for (const keyword of keywordList) {
        await api.addDFYKeyword(localProject.id, keyword.trim());
      }
      
      // Update onboarding
      await api.updateDFYOnboarding(localProject.id, {
        target_keywords: keywordList
      });
      
      toast.success(`Added ${keywordList.length} keywords`);
      setShowKeywordsDialog(false);
      setNewKeywords('');
      refreshProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add keywords');
    } finally {
      setLoading(false);
    }
  };

  // Handle content preferences
  const handleSaveContentPrefs = async () => {
    setLoading(true);
    try {
      await api.updateDFYOnboarding(localProject.id, {
        content_preferences: contentPrefs
      });
      toast.success('Content preferences saved');
      setShowContentPrefsDialog(false);
      refreshProject();
    } catch (error) {
      toast.error('Failed to save content preferences');
    } finally {
      setLoading(false);
    }
  };

  // Handle website access confirmation
  const handleConfirmAccess = async () => {
    setLoading(true);
    try {
      await api.updateDFYOnboarding(localProject.id, {
        website_access_granted: true
      });
      toast.success('Website access confirmed');
      setShowAccessDialog(false);
      refreshProject();
    } catch (error) {
      toast.error('Failed to update access status');
    } finally {
      setLoading(false);
    }
  };

  // Handle GSC connection
  const handleConnectGSC = async () => {
    setLoading(true);
    try {
      // Initiate OAuth flow with Emergent Google Auth
      const response = await api.initiateGSCConnection(localProject.id);
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to initiate GSC connection');
      setLoading(false);
    }
  };

  // Handle adding single keyword to rankings
  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }
    
    setLoading(true);
    try {
      await api.addDFYKeyword(localProject.id, newKeyword.trim());
      toast.success('Keyword added');
      setShowAddKeywordDialog(false);
      setNewKeyword('');
      refreshProject();
    } catch (error) {
      toast.error('Failed to add keyword');
    } finally {
      setLoading(false);
    }
  };

  // Handle adding content
  const handleAddContent = async () => {
    if (!newContent.title.trim()) {
      toast.error('Please enter a content title');
      return;
    }
    
    setLoading(true);
    try {
      await api.addDFYContent(localProject.id, newContent.title, newContent.type);
      toast.success('Content request added');
      setShowAddContentDialog(false);
      setNewContent({ title: '', type: 'blog' });
      refreshProject();
    } catch (error) {
      toast.error('Failed to add content');
    } finally {
      setLoading(false);
    }
  };

  // Handle content approval
  const handleApproveContent = async (contentId) => {
    setLoading(true);
    try {
      await api.updateDFYContent(localProject.id, contentId, 'approved');
      toast.success('Content approved');
      refreshProject();
    } catch (error) {
      toast.error('Failed to approve content');
    } finally {
      setLoading(false);
    }
  };

  // Handle content revision
  const handleReviseContent = async (contentId) => {
    setLoading(true);
    try {
      await api.updateDFYContent(localProject.id, contentId, 'revising');
      toast.success('Content sent for revision');
      refreshProject();
    } catch (error) {
      toast.error('Failed to update content');
    } finally {
      setLoading(false);
    }
  };

  // Handle report generation
  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      await api.generateDFYReport(localProject.id, {
        report_title: `${localProject.project_name} - SEO Report`,
        date_range: 'last_30_days',
        sections: ['overview', 'keywords', 'website_audit', 'backlinks', 'search_console']
      });
      toast.success('Report generation started');
      setShowReportDialog(false);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingAction = (action) => {
    if (isDemo) {
      toast.info('This is a demo - actions are disabled');
      return;
    }
    
    switch (action) {
      case 'keywords':
        setShowKeywordsDialog(true);
        break;
      case 'content':
        setShowContentPrefsDialog(true);
        break;
      case 'access':
        setShowAccessDialog(true);
        break;
      case 'gsc':
        setShowGSCModal(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects</Button>
        <div className="flex items-center gap-3">
          {isDemo && <Badge variant="outline" className="text-amber-400 border-amber-400"><Lock className="w-3 h-3 mr-1" /> Demo Mode</Badge>}
          <div className="text-right">
            <h2 className="text-xl font-bold text-white">{localProject.project_name}</h2>
            <p className="text-gray-400 text-sm">{localProject.website_url}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 bg-white/5 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn("px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2", activeTab === tab.id ? "bg-white/10 text-white" : "text-gray-400 hover:text-white")}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-6 space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Welcome! Let&apos;s get everything set up.</h3>
              <p className="text-gray-400">Complete these steps to get started with your {localProject.service_type?.toUpperCase()} campaign</p>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-gray-400 text-sm">Progress</span>
              <Progress value={localProject.onboarding?.progress || 0} className="flex-1 h-3" />
              <span className="text-primary font-medium">{localProject.onboarding?.progress || 0}% Complete</span>
            </div>

            <div className="space-y-3">
              {[
                { key: 'keywords', title: 'Add Your Target Keywords', completed: (localProject.onboarding?.keywords_count || 0) > 0, description: localProject.onboarding?.keywords_count > 0 ? `✓ ${localProject.onboarding.keywords_count} keywords added` : 'Add the keywords you want to rank for', action: 'keywords' },
                { key: 'content', title: 'Provide Your Content Preferences', completed: Object.keys(localProject.onboarding?.content_preferences || {}).length > 0, description: Object.keys(localProject.onboarding?.content_preferences || {}).length > 0 ? '✓ Preferences saved' : 'Tell us about your brand voice and content style', action: 'content' },
                { key: 'access', title: 'Provide Access To Your Website', completed: localProject.onboarding?.website_access_granted, description: localProject.onboarding?.website_access_granted ? '✓ Access granted' : `Invite us as editor: ${localProject.onboarding?.website_access_email || 'siteaccess@agencyreport.ai'}`, action: 'access' },
                { key: 'gsc', title: 'Connect Google Search Console', completed: localProject.onboarding?.gsc_connected, description: localProject.onboarding?.gsc_connected ? '✓ Connected' : 'Connect GSC to track your search performance', action: 'gsc' }
              ].map((item) => (
                <div key={item.key} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg">
                  <div className={cn("w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0", item.completed ? "bg-green-500 border-green-500" : "border-gray-500")}>
                    {item.completed && <CheckCircle2 className="w-4 h-4 text-white" />}
                  </div>
                  <div className="flex-1">
                    <h4 className={cn("font-medium", item.completed ? "text-green-400" : "text-white")}>{item.title}</h4>
                    <p className="text-gray-400 text-sm mt-1">{item.description}</p>
                    {!item.completed && (
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => handleOnboardingAction(item.action)} disabled={isDemo}>
                        {item.key === 'gsc' ? 'Connect' : item.key === 'access' ? 'Confirm Access' : 'Complete'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rankings Tab */}
      {activeTab === 'rankings' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-white">Tracked Keywords</CardTitle>
                <CardDescription>
                  {localProject.rankings?.top_3 || 0} in top 3, {localProject.rankings?.top_10 || 0} in top 10, {localProject.rankings?.top_100 || 0} in top 100
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowAddKeywordDialog(true)} disabled={isDemo}>
                <Plus className="w-4 h-4 mr-1" /> Add Keywords
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(localProject.rankings?.keywords || []).length === 0 ? (
              <div className="text-center py-8">
                <Target className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400 mb-4">No keywords tracked yet.</p>
                <Button onClick={() => setShowAddKeywordDialog(true)} disabled={isDemo}>
                  <Plus className="w-4 h-4 mr-2" /> Add Your First Keyword
                </Button>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="p-3 text-gray-400 font-medium">Keyword</th>
                    <th className="p-3 text-gray-400 font-medium">Desktop</th>
                    <th className="p-3 text-gray-400 font-medium">Mobile</th>
                    <th className="p-3 text-gray-400 font-medium">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {localProject.rankings.keywords.map((kw) => (
                    <tr key={kw.id} className="border-b border-white/5">
                      <td className="p-3 text-cyan-400">{kw.keyword}</td>
                      <td className="p-3 text-white">{kw.desktop_rank ? `#${kw.desktop_rank}` : '-'}</td>
                      <td className="p-3 text-white">{kw.mobile_rank ? `#${kw.mobile_rank}` : '-'}</td>
                      <td className="p-3">
                        <span className={cn("font-bold", (kw.net_change || 0) > 0 ? "text-green-400" : (kw.net_change || 0) < 0 ? "text-red-400" : "text-white")}>
                          {(kw.net_change || 0) > 0 ? '+' : ''}{kw.net_change || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Audits Tab */}
      {activeTab === 'audits' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-white">Website Audit</CardTitle>
                <CardDescription>{localProject.audits?.issues?.length || 0} issues found</CardDescription>
              </div>
              <Button size="sm" onClick={() => toast.info('Audit will be run by our team')} disabled={isDemo}>
                <Play className="w-4 h-4 mr-1" /> Request Audit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(localProject.audits?.issues || []).length === 0 ? (
              <div className="text-center py-8">
                <FileSearch className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">No audit data yet. Our team will run an audit after you grant website access.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {localProject.audits.issues.map((issue) => (
                  <div key={issue.id} className="p-3 bg-white/5 rounded-lg flex items-start gap-3">
                    <Badge className={cn(issue.severity === 'critical' && "bg-red-500/20 text-red-400", issue.severity === 'warning' && "bg-yellow-500/20 text-yellow-400")}>{issue.severity}</Badge>
                    <div>
                      <p className="text-white font-medium">{issue.title}</p>
                      <p className="text-gray-400 text-sm">{issue.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Backlinks Tab */}
      {activeTab === 'backlinks' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Backlinks</CardTitle>
            <CardDescription>{localProject.backlinks?.total?.toLocaleString() || 0} total backlinks from {localProject.backlinks?.referring_domains || 0} domains</CardDescription>
          </CardHeader>
          <CardContent>
            {(localProject.backlinks?.top_domains || []).length === 0 ? (
              <div className="text-center py-8">
                <Link2 className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">No backlink data yet. Our team will scan for backlinks.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="p-3 text-gray-400 font-medium">Domain</th>
                    <th className="p-3 text-gray-400 font-medium">DR</th>
                    <th className="p-3 text-gray-400 font-medium">Type</th>
                    <th className="p-3 text-gray-400 font-medium">First Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {localProject.backlinks.top_domains.map((d, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="p-3 text-white">{d.domain}</td>
                      <td className="p-3 text-green-400 font-bold">{d.domain_rank}</td>
                      <td className="p-3 text-gray-400">{d.link_type}</td>
                      <td className="p-3 text-gray-400">{new Date(d.first_seen).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Content Tab */}
      {activeTab === 'content' && (
        <DFYContentTabFunctional 
          project={localProject} 
          isDemo={isDemo}
          onAddContent={() => setShowAddContentDialog(true)}
          onApprove={handleApproveContent}
          onRevise={handleReviseContent}
        />
      )}

      {/* Search Console Tab */}
      {activeTab === 'search_console' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Google Search Console</CardTitle>
            <CardDescription>{localProject.search_console?.connected ? `Connected: ${localProject.search_console.property}` : 'Not connected'}</CardDescription>
          </CardHeader>
          <CardContent>
            {!localProject.search_console?.connected ? (
              <div className="text-center py-8">
                <LineChart className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400 mb-4">Connect Google Search Console to see your search performance data.</p>
                <Button onClick={() => setShowGSCModal(true)} disabled={isDemo}>Connect Search Console</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white">{localProject.search_console.total_impressions?.toLocaleString() || 0}</div>
                    <div className="text-gray-400 text-sm">Impressions</div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white">{localProject.search_console.total_clicks?.toLocaleString() || 0}</div>
                    <div className="text-gray-400 text-sm">Clicks</div>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white">{localProject.search_console.avg_ctr || 0}%</div>
                    <div className="text-gray-400 text-sm">CTR</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Reports</CardTitle>
            <CardDescription>Generate and download reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(localProject.reports || []).length > 0 && (
                <div className="space-y-2 mb-4">
                  <h4 className="text-white font-medium">Generated Reports</h4>
                  {localProject.reports.map((report) => (
                    <div key={report.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white">{report.title}</p>
                        <p className="text-gray-400 text-xs">{new Date(report.generated_at).toLocaleDateString()}</p>
                      </div>
                      <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" /> Download</Button>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={() => setShowReportDialog(true)} disabled={isDemo}>
                <BarChart3 className="w-4 h-4 mr-2" /> Generate New Report
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Project Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input value={localProject.project_name} disabled />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Input value={localProject.status} disabled className="capitalize" />
              </div>
              <div className="space-y-2">
                <Label>Website URL</Label>
                <Input value={localProject.website_url} disabled />
              </div>
              <div className="space-y-2">
                <Label>Created</Label>
                <Input value={new Date(localProject.created_at).toLocaleDateString()} disabled />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Keywords Dialog */}
      <Dialog open={showKeywordsDialog} onOpenChange={setShowKeywordsDialog}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Target Keywords</DialogTitle>
            <DialogDescription>Enter the keywords you want to rank for (one per line)</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="keyword 1&#10;keyword 2&#10;keyword 3"
              value={newKeywords}
              onChange={(e) => setNewKeywords(e.target.value)}
              rows={6}
              className="resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">Enter each keyword on a new line</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKeywordsDialog(false)}>Cancel</Button>
            <Button onClick={handleAddOnboardingKeywords} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Keywords
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content Preferences Dialog */}
      <Dialog open={showContentPrefsDialog} onOpenChange={setShowContentPrefsDialog}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Content Preferences</DialogTitle>
            <DialogDescription>Tell us about your brand voice and content style</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Tone</Label>
              <Select value={contentPrefs.tone} onValueChange={(v) => setContentPrefs({...contentPrefs, tone: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="authoritative">Authoritative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Style</Label>
              <Select value={contentPrefs.style} onValueChange={(v) => setContentPrefs({...contentPrefs, style: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="informative">Informative</SelectItem>
                  <SelectItem value="persuasive">Persuasive</SelectItem>
                  <SelectItem value="educational">Educational</SelectItem>
                  <SelectItem value="entertaining">Entertaining</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Input 
                placeholder="e.g., Small business owners, homeowners..."
                value={contentPrefs.target_audience}
                onChange={(e) => setContentPrefs({...contentPrefs, target_audience: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Topics to Avoid (optional)</Label>
              <Input 
                placeholder="e.g., Competitor mentions, pricing..."
                value={contentPrefs.avoid_topics}
                onChange={(e) => setContentPrefs({...contentPrefs, avoid_topics: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContentPrefsDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveContentPrefs} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Website Access Dialog */}
      <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Website Access</DialogTitle>
            <DialogDescription>Grant us access to make changes to your website</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
              <p className="text-cyan-400 font-medium mb-2">Please invite us as an editor:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-black/30 rounded text-white text-sm">
                  {localProject.onboarding?.website_access_email || 'siteaccess@agencyreport.ai'}
                </code>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(localProject.onboarding?.website_access_email || 'siteaccess@agencyreport.ai');
                  toast.success('Email copied!');
                }}>
                  Copy
                </Button>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              After you&apos;ve added us as an editor to your website (WordPress, Wix, Squarespace, etc.), click the button below to confirm.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccessDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmAccess} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              I&apos;ve Granted Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GSC Modal */}
      <Dialog open={showGSCModal} onOpenChange={setShowGSCModal}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Connect Google Search Console</DialogTitle>
            <DialogDescription>Connect your GSC to track search performance</DialogDescription>
          </DialogHeader>
          <div className="py-4 text-center">
            <LineChart className="w-16 h-16 mx-auto text-primary mb-4" />
            <p className="text-gray-400 mb-4">Click below to connect your Google Search Console account.</p>
            <Button onClick={handleConnectGSC} disabled={loading || isDemo}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Connect with Google
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGSCModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Keyword Dialog */}
      <Dialog open={showAddKeywordDialog} onOpenChange={setShowAddKeywordDialog}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Keyword</DialogTitle>
            <DialogDescription>Add a keyword to track rankings</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter keyword..."
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddKeywordDialog(false)}>Cancel</Button>
            <Button onClick={handleAddKeyword} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Keyword
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Content Dialog */}
      <Dialog open={showAddContentDialog} onOpenChange={setShowAddContentDialog}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Request Content</DialogTitle>
            <DialogDescription>Request a new piece of content from our team</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Content Title / Topic</Label>
              <Input
                placeholder="e.g., 10 Tips for Home Maintenance"
                value={newContent.title}
                onChange={(e) => setNewContent({...newContent, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Content Type</Label>
              <Select value={newContent.type} onValueChange={(v) => setNewContent({...newContent, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blog">Blog Post</SelectItem>
                  <SelectItem value="web_copy">Web Copy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddContent} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Request Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-card border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Generate Report</DialogTitle>
            <DialogDescription>Generate a comprehensive SEO report</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-400 mb-4">This will generate a report including:</p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Overview & Summary</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Keyword Rankings</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Website Audit Results</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Backlink Analysis</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-400" /> Search Console Data</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button onClick={handleGenerateReport} disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== DFY CONTENT TAB (FUNCTIONAL) ====================
const DFYContentTabFunctional = ({ project, isDemo, onAddContent, onApprove, onRevise }) => {
  const [filter, setFilter] = useState('all');
  const articles = project.content?.articles || [];
  
  const filteredArticles = articles.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter;
  });

  const statusCounts = {
    all: articles.length,
    needs_approval: articles.filter(a => a.status === 'needs_approval').length,
    revising: articles.filter(a => a.status === 'revising').length,
    approved: articles.filter(a => a.status === 'approved').length,
    published: articles.filter(a => a.status === 'published').length,
    planned: articles.filter(a => a.status === 'planned').length
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">Content</CardTitle>
          <Button size="sm" onClick={onAddContent} disabled={isDemo}>
            <Plus className="w-4 h-4 mr-1" /> Request Topic
          </Button>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          {['all', 'planned', 'needs_approval', 'revising', 'approved', 'published'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'ghost'} size="sm" onClick={() => setFilter(f)} className="capitalize">
              {f.replace('_', ' ')} ({statusCounts[f] || 0})
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {filteredArticles.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400 mb-4">No content in this category.</p>
            <Button onClick={onAddContent} disabled={isDemo}>
              <Plus className="w-4 h-4 mr-2" /> Request Your First Content
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredArticles.map((article) => (
              <div key={article.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-white font-medium">{article.title}</p>
                    <p className="text-gray-400 text-xs">{new Date(article.date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn(
                    article.status === 'needs_approval' && "border-amber-500 text-amber-400",
                    article.status === 'revising' && "border-blue-500 text-blue-400",
                    article.status === 'approved' && "border-green-500 text-green-400",
                    article.status === 'published' && "border-cyan-500 text-cyan-400",
                    article.status === 'planned' && "border-gray-500 text-gray-400"
                  )}>
                    {(article.status || 'planned').replace('_', ' ')}
                  </Badge>
                  {article.status === 'needs_approval' && !isDemo && (
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => onApprove(article.id)} title="Approve">
                        <CheckCircle2 className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => onRevise(article.id)} title="Request Revision">
                        <RefreshCw className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== DFY CONTENT TAB ====================
const DFYContentTab = ({ project, isDemo }) => {
  const [filter, setFilter] = useState('all');
  const articles = project.content?.articles || [];
  
  const filteredArticles = articles.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter;
  });

  const statusCounts = {
    all: articles.length,
    needs_approval: articles.filter(a => a.status === 'needs_approval').length,
    revising: articles.filter(a => a.status === 'revising').length,
    approved: articles.filter(a => a.status === 'approved').length,
    published: articles.filter(a => a.status === 'published').length,
    planned: articles.filter(a => a.status === 'planned').length
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">Content</CardTitle>
          <Button size="sm" disabled={isDemo}><Plus className="w-4 h-4 mr-1" /> Request Topic</Button>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          {['all', 'planned', 'needs_approval', 'revising', 'approved', 'published'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'ghost'} size="sm" onClick={() => setFilter(f)} className="capitalize">
              {f.replace('_', ' ')} ({statusCounts[f] || 0})
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {filteredArticles.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No content in this category.</p>
        ) : (
          <div className="space-y-2">
            {filteredArticles.map((article) => (
              <div key={article.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-white font-medium">{article.title}</p>
                    <p className="text-gray-400 text-xs">{new Date(article.date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={cn(
                    article.status === 'needs_approval' && "border-amber-500 text-amber-400",
                    article.status === 'revising' && "border-blue-500 text-blue-400",
                    article.status === 'approved' && "border-green-500 text-green-400",
                    article.status === 'published' && "border-cyan-500 text-cyan-400",
                    article.status === 'planned' && "border-gray-500 text-gray-400"
                  )}>
                    {(article.status || 'planned').replace('_', ' ')}
                  </Badge>
                  {article.status === 'needs_approval' && (
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" disabled={isDemo}><CheckCircle2 className="w-3 h-3" /></Button>
                      <Button variant="outline" size="sm" disabled={isDemo}><RefreshCw className="w-3 h-3" /></Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== MAIN PAGE COMPONENT ====================
const RankingSystemPage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [dfyProjects, setDfyProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('tools');
  const [pricing, setPricing] = useState(null);
  const [demoProject, setDemoProject] = useState(null);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, dfyRes, pricingRes] = await Promise.all([
        api.getRankingProjects(),
        api.getDFYProjects(),
        api.getDFYPricing()
      ]);
      setProjects(projectsRes.data.map(p => ({ ...p, management_type: 'diy' })));
      setDfyProjects(dfyRes.data.map(p => ({ ...p, management_type: 'dfy' })));
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const allProjects = [...projects, ...dfyProjects].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handleProjectCreated = (project) => {
    if (project.management_type === 'dfy') {
      setDfyProjects(prev => [project, ...prev]);
    } else {
      setProjects(prev => [project, ...prev]);
    }
    setSelectedProject(project);
    setActiveMainTab('project');
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setActiveMainTab('project');
    setShowDemo(false);
  };

  const handleDeleteProject = async (project) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      if (project.management_type === 'dfy') {
        await api.deleteDFYProject(project.id);
        setDfyProjects(prev => prev.filter(p => p.id !== project.id));
      } else {
        await api.deleteRankingProject(project.id);
        setProjects(prev => prev.filter(p => p.id !== project.id));
      }
      if (selectedProject?.id === project.id) {
        setSelectedProject(null);
        setActiveMainTab('projects');
      }
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleToggleMode = async (project) => {
    if (project.management_type === 'diy') {
      // Open create dialog with DFY pre-selected and project info
      toast.info('Upgrade to DFY coming soon - contact support for now');
    } else {
      toast.info('Switch to DIY - you will manage everything yourself. Contact support to cancel subscription.');
    }
  };

  // Handle subscription checkout
  const handleSubscribe = async (project) => {
    try {
      toast.loading('Creating checkout session...', { id: 'checkout' });
      const response = await api.createDFYCheckout(project.id);
      toast.dismiss('checkout');
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.dismiss('checkout');
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
    }
  };

  // Check for payment success/cancel and GSC callback on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('project');
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    const gscCallback = params.get('gsc');

    // Handle GSC OAuth callback
    if (projectId && gscCallback === 'callback') {
      // Check for session_id in URL hash (Emergent OAuth returns it in hash)
      const hash = window.location.hash;
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      const oauthSessionId = hashParams.get('session_id');
      
      if (oauthSessionId) {
        const handleGSCCallback = async () => {
          try {
            toast.loading('Connecting Google Search Console...', { id: 'gsc' });
            await api.handleGSCCallback(projectId, oauthSessionId);
            toast.dismiss('gsc');
            toast.success('Google Search Console connected successfully!');
            fetchData();
          } catch (error) {
            toast.dismiss('gsc');
            toast.error('Failed to connect Google Search Console');
          }
        };
        handleGSCCallback();
      }
      // Clean up URL
      window.history.replaceState({}, '', '/ranking');
    }

    // Handle payment callback
    if (projectId && payment === 'success' && sessionId) {
      // Poll payment status
      const checkPayment = async () => {
        try {
          const response = await api.getDFYPaymentStatus(projectId, sessionId);
          if (response.data.payment_status === 'paid') {
            toast.success('Payment successful! Your subscription is now active.');
            // Refresh projects
            fetchData();
          }
        } catch (error) {
          console.error('Payment status check failed:', error);
        }
      };
      checkPayment();
      // Clean up URL
      window.history.replaceState({}, '', '/ranking');
    } else if (payment === 'cancelled') {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, '', '/ranking');
    }
  }, []);

  const handleTakeTour = async () => {
    try {
      const response = await api.getDemoProject();
      setDemoProject(response.data);
      setSelectedProject(response.data);
      setShowDemo(true);
      setActiveMainTab('project');
    } catch (error) {
      toast.error('Failed to load demo');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">AI Horse-Powered Ranking System</h1>
              <p className="text-gray-400">SEO, Google Ads, Google Maps - DIY or Done For You</p>
            </div>
          </div>
        </div>

        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="mb-6" data-tour="ranking-tabs">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="tools" className="data-[state=active]:bg-primary/20">
              <Zap className="w-4 h-4 mr-2" /> Free Tools
            </TabsTrigger>
            <TabsTrigger value="projects" className="data-[state=active]:bg-primary/20">
              <Globe className="w-4 h-4 mr-2" /> Projects ({allProjects.length})
            </TabsTrigger>
            <TabsTrigger value="sops" className="data-[state=active]:bg-primary/20">
              <BookMarked className="w-4 h-4 mr-2" /> SOPs & Guides
            </TabsTrigger>
            {selectedProject && (
              <TabsTrigger value="project" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                <Target className="w-4 h-4 mr-2" /> {selectedProject.project_name || selectedProject.name}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="tools" className="mt-6">
            <FreeToolsSection />
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-400" /> My Projects
              </h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleTakeTour} data-tour="take-tour"><Play className="w-4 h-4 mr-2" /> Take a Tour</Button>
                <Button onClick={() => setShowCreateDialog(true)} data-tour="add-project"><Plus className="w-4 h-4 mr-2" /> Add Project</Button>
              </div>
            </div>

            {allProjects.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">No Projects Yet</h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto">Create your first project to start tracking SEO, Google Ads, and local rankings. Choose DIY (free) or Done For You (managed).</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={handleTakeTour}><Play className="w-4 h-4 mr-2" /> Take a Tour</Button>
                    <Button onClick={() => setShowCreateDialog(true)} size="lg"><Plus className="w-5 h-5 mr-2" /> Create Your First Project</Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} onView={handleViewProject} onDelete={handleDeleteProject} onToggleMode={handleToggleMode} onSubscribe={handleSubscribe} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sops" className="mt-6">
            <SOPViewer />
          </TabsContent>

          {selectedProject && (
            <TabsContent value="project" className="mt-6">
              {(selectedProject.management_type === 'dfy' || showDemo) ? (
                <DFYProjectDetailView 
                  project={selectedProject} 
                  isDemo={showDemo}
                  onBack={() => { setSelectedProject(null); setActiveMainTab('projects'); setShowDemo(false); }}
                />
              ) : (
                <DIYProjectDetailView 
                  project={selectedProject} 
                  onBack={() => { setSelectedProject(null); setActiveMainTab('projects'); }}
                />
              )}
            </TabsContent>
          )}
        </Tabs>

        <CreateProjectDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} onCreated={handleProjectCreated} pricing={pricing} />
      </div>
    </DashboardLayout>
  );
};

export default RankingSystemPage;
